import { type FC } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage, FormattedNumber, FormattedDate } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import InfoCircle from 'dibs-icons/exports/InfoCircle.ts';
import { TradeBenefitsHeroStatusContact } from './TradeBenefitsHeroStatusContact.tsx';
import { TradeBenefitsHeroStatusProgressBar } from './TradeBenefitsHeroStatusProgressBar.tsx';

import { TRADE_STATUS } from '../../tradeBenefitsConstants.ts';
import { getNextRewardsData } from '../../../sharedRewards/helpers/rewardsStatusHelper.tsx';
import { useServerVars } from '../../../shared/ServerVarsContext.tsx';

import { type TradeBenefitsHeroStatus_viewer$key } from './__generated__/TradeBenefitsHeroStatus_viewer.graphql.ts';
import { type TradeBenefitsHeroStatus_tradeFirm$key } from './__generated__/TradeBenefitsHeroStatus_tradeFirm.graphql.ts';

import styles from './TradeBenefitsHeroStatus.scss';
import dibsCss from 'dibs-css';

const getPointsResetDate = (): Date => new Date(new Date().getFullYear() + 1, 0, 1);

const viewerFragment = graphql`
    fragment TradeBenefitsHeroStatus_viewer on Viewer {
        ...TradeBenefitsHeroStatusContact_viewer
    }
`;

const tradeFirmFragment = graphql`
    fragment TradeBenefitsHeroStatus_tradeFirm on TradeFirmType {
        rewards {
            nextYearProgram {
                programInfo {
                    code
                }
            }
            currentYearSpend {
                amount
            }
            currentProgram {
                programInfo {
                    code
                    shortCopy
                }
            }
            ...rewardsStatusHelper_rewards
            ...TradeBenefitsHeroStatusProgressBar_rewards
        }
        ...TradeBenefitsHeroStatusContact_tradeFirm
    }
`;

const TradeBenefitsHeroStatus: FC<{
    viewer: TradeBenefitsHeroStatus_viewer$key;
    tradeFirm: TradeBenefitsHeroStatus_tradeFirm$key | null | undefined;
}> = ({ viewer: viewerRef, tradeFirm: tradeFirmRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const { isMobile } = useServerVars();
    const { rewards } = tradeFirm || {};
    const currentYearSpend = Math.floor(rewards?.currentYearSpend?.amount || 0);
    const pointsResetDate = getPointsResetDate();
    const programCopy = rewards?.currentProgram?.programInfo?.shortCopy;
    const isPlatinum = rewards?.currentProgram?.programInfo?.code === TRADE_STATUS.PLATINUM;
    const hasSecuredPlatinumNextYear =
        rewards?.nextYearProgram?.programInfo?.code === TRADE_STATUS.PLATINUM;

    const { nextStatus, rewardPoints } = rewards ? getNextRewardsData(rewards) : {};
    const upcomingYear = <FormattedDate value={pointsResetDate} year="numeric" />;

    const platinumStatusCopy = hasSecuredPlatinumNextYear ? (
        <FormattedMessage
            id="abt.TradeBenefitsHeroStatus.rewardPointsMax"
            defaultMessage="You have secured <bold>{programCopy} Status</bold> for {upcomingYear}"
            values={{
                programCopy,
                upcomingYear,
                bold: content => (
                    <span
                        className={
                            isMobile
                                ? dibsCss.sassyFontBodyXSmallHeavy
                                : dibsCss.sassyFontBodySmallHeavy
                        }
                    >
                        {content}
                    </span>
                ),
            }}
        />
    ) : (
        <FormattedMessage
            id="abt.TradeBenefitsHeroStatus.platinumPointsAway"
            defaultMessage="You're {rewardPoints} points away from <bold>{programCopy} Status</bold> for {upcomingYear}"
            values={{
                rewardPoints,
                programCopy: programCopy,
                upcomingYear,
                bold: content => (
                    <span
                        className={
                            isMobile
                                ? dibsCss.sassyFontBodyXSmallHeavy
                                : dibsCss.sassyFontBodySmallHeavy
                        }
                    >
                        {content}
                    </span>
                ),
            }}
        />
    );

    return (
        <div className={classnames(dibsCss.roundedXsmall, dibsCss.bgWhite)}>
            {rewards && (
                <div
                    className={classnames(
                        styles.status,
                        dibsCss.pxMedium,
                        dibsCss.ptMedium,
                        dibsCss.pbSmall,
                        dibsCss.borderB,
                        dibsCss.borderBSolid
                    )}
                >
                    <div
                        className={classnames(
                            styles.top,
                            dibsCss.flex,
                            dibsCss.justifyBetween,
                            dibsCss.itemsCenter
                        )}
                    >
                        <div className={dibsCss.sassyFontBodyLargeNormal}>
                            <FormattedMessage
                                id="abt.TradeBenefitsHeroStatus.youHavePoints"
                                defaultMessage="You have <bold>{currentYearSpend} points</bold>"
                                values={{
                                    currentYearSpend: <FormattedNumber value={currentYearSpend} />,
                                    bold: content => (
                                        <span
                                            className={dibsCss.sassyFontBodyLargeHeavy}
                                            data-tn="trade-benefits-points-earned"
                                        >
                                            {content}
                                        </span>
                                    ),
                                }}
                            />
                        </div>
                        {!isMobile && (
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.itemsCenter,
                                    dibsCss.gapXxsmall,
                                    dibsCss.sassyFontBodySmallNormal
                                )}
                            >
                                <FormattedMessage
                                    id="abt.TradeBenefitsHeroStatus.pointsResetDate"
                                    defaultMessage="Points reset on {pointsResetDate}"
                                    values={{
                                        pointsResetDate: <FormattedDate value={pointsResetDate} />,
                                    }}
                                />
                                <Link href="#faq-points-expiration">
                                    <InfoCircle variation="14" />
                                </Link>
                            </div>
                        )}
                    </div>
                    <TradeBenefitsHeroStatusProgressBar rewards={rewards} />
                    {!isMobile && (
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.justifyBetween,
                                dibsCss.sassyFontBodySmallNormal
                            )}
                        >
                            <div
                                className={dibsCss.textBodySmallHeavy}
                                data-tn="trade-benefits-current-program"
                            >
                                {!isPlatinum && programCopy}
                            </div>
                            <div data-tn="trade-benefits-next-status">
                                {isPlatinum ? (
                                    platinumStatusCopy
                                ) : (
                                    <FormattedMessage
                                        id="abt.TradeBenefitsHeroStatus.rewardPointsDesktop"
                                        defaultMessage="{rewardPoints} points away from <bold>{nextStatus}</bold>"
                                        values={{
                                            rewardPoints,
                                            nextStatus,
                                            bold: content => (
                                                <span className={dibsCss.sassyFontBodySmallHeavy}>
                                                    {content}
                                                </span>
                                            ),
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {isMobile && (
                        <div className={dibsCss.sassyFontBodyXSmallNormal}>
                            <div>
                                {isPlatinum ? (
                                    platinumStatusCopy
                                ) : (
                                    <FormattedMessage
                                        id="abt.TradeBenefitsHeroStatus.rewardPointsMobile"
                                        defaultMessage="You’re {rewardPoints} points away from <bold>{nextStatus} Status</bold>"
                                        values={{
                                            rewardPoints,
                                            nextStatus,
                                            bold: content => (
                                                <span className={dibsCss.sassyFontBodyXSmallHeavy}>
                                                    {content}
                                                </span>
                                            ),
                                        }}
                                    />
                                )}
                            </div>
                            <FormattedMessage
                                id="abt.TradeBenefitsHeroStatus.pointsResetDate"
                                defaultMessage="Points reset on {pointsResetDate}"
                                values={{
                                    pointsResetDate: <FormattedDate value={pointsResetDate} />,
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
            {!isMobile && (
                <div
                    className={classnames(
                        dibsCss.pbMedium,
                        dibsCss.pxMedium,
                        rewards
                            ? dibsCss.ptSmall
                            : [styles.contact, dibsCss.ptMedium, dibsCss.flex, dibsCss.itemsCenter]
                    )}
                >
                    <TradeBenefitsHeroStatusContact viewer={viewer} tradeFirm={tradeFirm} />
                </div>
            )}
        </div>
    );
};

export { TradeBenefitsHeroStatus };
